import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react'
import {
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  FileDoneOutlined,
  FunnelPlotOutlined,
  InboxOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  SwapOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import cn from 'classnames'
import Button from '@UI/New/Button'
import type { LeadState } from '@/api/types/crm/crm.types'
import classes from './LeadProfileModalFooter.module.css'

const antIconPointerProps = {
  onPointerEnterCapture: undefined,
  onPointerLeaveCapture: undefined,
}

const DownIcon = DownOutlined as unknown as FC<{ className?: string }>

type FooterMenuItem = NonNullable<MenuProps['items']>[number]
type FooterDropdownKey = 'state' | 'funnels' | 'deals'

const FOOTER_DROPDOWN_ATTR = 'data-lead-footer-dropdown'

interface LeadProfileModalFooterProps {
  canEditFields: boolean
  canViewControlButtons: boolean
  canDelete: boolean
  canUseFunnels: boolean
  canRemoveFunnels: boolean
  multipleDealsEnabled: boolean
  canRemoveDeal: boolean
  leadState: LeadState | string | undefined
  onSave: () => void
  onCancel: () => void
  onMoveToInWork: () => void
  onChangeLeadState: () => void
  onMoveToClient: () => void
  onOpenDeleteLead: () => void
  onAddFunnel: () => void
  onRemoveFunnel: () => void
  onAddDeal: () => void
  onRemoveDeal: () => void
}

const FooterGroup: FC<{
  children: ReactNode
  className?: string
}> = ({ children, className }) => (
  <div className={cn(classes.group, className)}>{children}</div>
)

const MenuTriggerButton: FC<{
  label: string
  icon: ReactNode
}> = ({ label, icon }) => (
  <Button
    variant="action"
    color="gray"
    leftContent={icon}
    rightContent={<DownIcon className={classes.chevron} />}
  >
    {label}
  </Button>
)

const LeadProfileModalFooter: FC<LeadProfileModalFooterProps> = ({
  canEditFields,
  canViewControlButtons,
  canDelete,
  canUseFunnels,
  canRemoveFunnels,
  multipleDealsEnabled,
  canRemoveDeal,
  leadState,
  onSave,
  onCancel,
  onMoveToInWork,
  onChangeLeadState,
  onMoveToClient,
  onOpenDeleteLead,
  onAddFunnel,
  onRemoveFunnel,
  onAddDeal,
  onRemoveDeal,
}) => {
  const [openMenu, setOpenMenu] = useState<FooterDropdownKey | null>(null)

  // CRM Modal глушит bubble mousedown — закрываем в capture.
  useEffect(() => {
    if (!openMenu) return

    const handleOutsidePointer = (event: Event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (target.closest(`[${FOOTER_DROPDOWN_ATTR}]`)) return
      if (target.closest('.ant-dropdown')) return
      setOpenMenu(null)
    }

    document.addEventListener('mousedown', handleOutsidePointer, true)
    document.addEventListener('touchstart', handleOutsidePointer, true)
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer, true)
      document.removeEventListener('touchstart', handleOutsidePointer, true)
    }
  }, [openMenu])

  const handleMenuOpenChange = (key: FooterDropdownKey, nextOpen: boolean) => {
    setOpenMenu(nextOpen ? key : null)
  }

  const stateMenuItems = useMemo((): FooterMenuItem[] => {
    if (!canViewControlButtons) return []

    if (leadState === 'new') {
      return [
        {
          key: 'to-in-work',
          icon: <UserAddOutlined {...antIconPointerProps} />,
          label: 'В работу',
          onClick: () => onMoveToInWork(),
        },
      ]
    }

    if (leadState === 'in_work') {
      return [
        {
          key: 'to-processed',
          icon: <InboxOutlined {...antIconPointerProps} />,
          label: 'В обработанные',
          onClick: () => onChangeLeadState(),
        },
        {
          key: 'to-client',
          icon: <UsergroupAddOutlined {...antIconPointerProps} />,
          label: 'В клиенты',
          onClick: () => onMoveToClient(),
        },
      ]
    }

    if (leadState === 'processed') {
      return [
        {
          key: 'back-to-work',
          icon: <RollbackOutlined {...antIconPointerProps} />,
          label: 'Вернуть в работу',
          onClick: () => onChangeLeadState(),
        },
        {
          key: 'to-client',
          icon: <UsergroupAddOutlined {...antIconPointerProps} />,
          label: 'В клиенты',
          onClick: () => onMoveToClient(),
        },
      ]
    }

    if (leadState === 'client') {
      return [
        {
          key: 'back-to-work',
          icon: <UserAddOutlined {...antIconPointerProps} />,
          label: 'Вернуть в работу',
          onClick: () => onChangeLeadState(),
        },
      ]
    }

    return []
  }, [
    canViewControlButtons,
    leadState,
    onChangeLeadState,
    onMoveToClient,
    onMoveToInWork,
  ])

  const funnelMenuItems = useMemo((): FooterMenuItem[] => {
    const items: FooterMenuItem[] = []
    if (canUseFunnels) {
      items.push({
        key: 'add-funnel',
        icon: <PlusOutlined {...antIconPointerProps} />,
        label: 'Добавить воронку',
        onClick: () => onAddFunnel(),
      })
    }
    if (canRemoveFunnels) {
      items.push({
        key: 'remove-funnel',
        icon: <DeleteOutlined {...antIconPointerProps} />,
        label: 'Удалить воронку',
        onClick: () => onRemoveFunnel(),
      })
    }
    return items
  }, [canRemoveFunnels, canUseFunnels, onAddFunnel, onRemoveFunnel])

  const dealMenuItems = useMemo((): FooterMenuItem[] => {
    if (!multipleDealsEnabled) return []
    const items: FooterMenuItem[] = []
    if (canUseFunnels) {
      items.push({
        key: 'add-deal',
        icon: <PlusOutlined {...antIconPointerProps} />,
        label: 'Добавить сделку',
        onClick: () => onAddDeal(),
      })
    }
    if (canRemoveFunnels) {
      items.push({
        key: 'remove-deal',
        icon: <DeleteOutlined {...antIconPointerProps} />,
        label: 'Удалить сделку',
        disabled: !canRemoveDeal,
        onClick: () => {
          if (!canRemoveDeal) return
          onRemoveDeal()
        },
      })
    }
    return items
  }, [
    canRemoveDeal,
    canRemoveFunnels,
    canUseFunnels,
    multipleDealsEnabled,
    onAddDeal,
    onRemoveDeal,
  ])

  if (!canEditFields && !canViewControlButtons) {
    return null
  }

  return (
    <div className={classes.root}>
      <FooterGroup className={classes.primaryGroup}>
        {canEditFields ? (
          <Button
            variant="action"
            color="secondary"
            leftContent={<SaveOutlined {...antIconPointerProps} />}
            onClick={onSave}
          >
            Сохранить
          </Button>
        ) : null}
        <Button
          variant="action"
          color="gray"
          leftContent={<CloseCircleOutlined {...antIconPointerProps} />}
          onClick={onCancel}
        >
          Отмена
        </Button>
      </FooterGroup>

      {stateMenuItems.length > 0 && (
        <FooterGroup>
          <Dropdown
            trigger={['click']}
            placement="topLeft"
            open={openMenu === 'state'}
            onOpenChange={(next) => handleMenuOpenChange('state', next)}
            menu={{
              items: stateMenuItems,
              onClick: () => setOpenMenu(null),
            }}
          >
            <span className={classes.triggerWrap} data-lead-footer-dropdown="state">
              <MenuTriggerButton
                label="Состояние"
                icon={<SwapOutlined {...antIconPointerProps} />}
              />
            </span>
          </Dropdown>
        </FooterGroup>
      )}

      {funnelMenuItems.length > 0 && (
        <FooterGroup>
          <Dropdown
            trigger={['click']}
            placement="topLeft"
            open={openMenu === 'funnels'}
            onOpenChange={(next) => handleMenuOpenChange('funnels', next)}
            menu={{
              items: funnelMenuItems,
              onClick: () => setOpenMenu(null),
            }}
          >
            <span
              className={classes.triggerWrap}
              data-lead-footer-dropdown="funnels"
            >
              <MenuTriggerButton
                label="Воронки"
                icon={<FunnelPlotOutlined {...antIconPointerProps} />}
              />
            </span>
          </Dropdown>
        </FooterGroup>
      )}

      {dealMenuItems.length > 0 && (
        <FooterGroup>
          <Dropdown
            trigger={['click']}
            placement="topLeft"
            open={openMenu === 'deals'}
            onOpenChange={(next) => handleMenuOpenChange('deals', next)}
            menu={{
              items: dealMenuItems,
              onClick: () => setOpenMenu(null),
            }}
          >
            <span className={classes.triggerWrap} data-lead-footer-dropdown="deals">
              <MenuTriggerButton
                label="Сделки"
                icon={<FileDoneOutlined {...antIconPointerProps} />}
              />
            </span>
          </Dropdown>
        </FooterGroup>
      )}

      {canDelete && (
        <FooterGroup className={classes.dangerGroup}>
          <Button
            variant="action"
            color="gray"
            leftContent={<DeleteOutlined {...antIconPointerProps} />}
            onClick={onOpenDeleteLead}
          >
            Удалить
          </Button>
        </FooterGroup>
      )}
    </div>
  )
}

export default LeadProfileModalFooter
